import { Box, Chip } from "@mui/material";
import React, { useState, useCallback } from "react";
import { LinearTagChip } from "components/LinearTagChip";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { WorkflowTagPickerAdapter } from "components/ManageTags/WorkflowTagPickerAdapter";
import { EntityUpdate } from "components/ManageTags/types";
import { useMutation } from "@apollo/client";
import { UPDATE_WORKFLOW_TAGS_BULK_MUTATION } from "routes/Staff/Fulfillment/WorkflowActivitiesTable/ManageWorkflowTags";
import { ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY } from "./index";
import { useAlerts } from "components/Alerts/AlertProvider";
import { LinearIssueStatusMap } from "hooks/useLinearIssueStatuses";

interface ErrorGroupWorkflowTagsCellProps {
  workflowId: string;
  tags: string[];
  linearStatusMap: LinearIssueStatusMap;
}

export const ErrorGroupWorkflowTagsCell = ({
  workflowId,
  tags,
  linearStatusMap,
}: ErrorGroupWorkflowTagsCellProps) => {
  const alerts = useAlerts();
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [updateWorkflowTags, { loading: updateTagsLoading }] = useMutation(
    UPDATE_WORKFLOW_TAGS_BULK_MUTATION,
    {
      refetchQueries: [ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY],
    }
  );

  const handleOpenTagsDialog = useCallback(() => {
    setTagsDialogOpen(true);
  }, []);

  const handleCloseTagsDialog = useCallback(() => {
    setTagsDialogOpen(false);
    setShowConfirmation(false);
  }, []);

  const handleConfirm = useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const handleCloseConfirmation = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  const handleSubmitTags = useCallback(
    async (updates: EntityUpdate[]) => {
      try {
        const workflowUpdate = updates.find((u) => u.id === workflowId);
        if (workflowUpdate) {
          await updateWorkflowTags({
            variables: {
              workflowUpdates: [
                {
                  workflowId,
                  tagNames: workflowUpdate.tags,
                },
              ],
            },
          });
          alerts.success("Tags updated successfully");
        }
        setTagsDialogOpen(false);
      } catch (error) {
        alerts.error("Failed to update tags", error);
      }
    },
    [workflowId, updateWorkflowTags, alerts]
  );

  const hasTags = tags.length > 0;

  if (hasTags) {
    // Show tags, max 3-4 visible, then overflow
    const maxVisibleTags = 3;
    const hasOverflow = tags.length > maxVisibleTags;
    const visibleTags = hasOverflow ? tags.slice(0, maxVisibleTags) : tags;
    const overflowCount = tags.length - maxVisibleTags;

    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            alignItems: "center",
            width: "100%",
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenTagsDialog();
          }}
        >
          {visibleTags.map((tag) => {
            return (
              <LinearTagChip
                key={tag}
                tag={tag}
                linearStatusMap={linearStatusMap}
                onNormalClick={() => {
                  handleOpenTagsDialog();
                }}
              />
            );
          })}
          {hasOverflow && (
            <Chip
              label={`+${overflowCount}`}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: "0.7rem",
                flexShrink: 0,
                minWidth: "auto",
              }}
            />
          )}
        </Box>
        <ManageTagsFormDialog
          open={tagsDialogOpen}
          onClose={handleCloseTagsDialog}
          onCloseConfirmation={handleCloseConfirmation}
          confirm={handleConfirm}
          showConfirmation={showConfirmation}
          withConfirmation={true}
          onSubmit={handleSubmitTags}
          selectedRows={[{ id: workflowId, tags }]}
          getEntityId={(row) => row.id}
          TagPickerComponent={WorkflowTagPickerAdapter}
          loading={updateTagsLoading}
          entityName="Workflow"
          localStorageKey="workflow-recent-tags"
          commonTags={["compliance", "engineering", "linear"]}
        />
      </>
    );
  }

  // No tags - show "+ add tag" on hover
  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          width: "100%",
          minHeight: 24,
        }}
      >
        <Chip
          label="+ add tag"
          size="small"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenTagsDialog();
          }}
          sx={{
            height: 20,
            fontSize: "0.7rem",
            cursor: "pointer",
            opacity: 0,
            transition: "opacity 0.2s",
            ".MuiDataGrid-row:hover &": {
              opacity: 1,
            },
          }}
        />
      </Box>
      <ManageTagsFormDialog
        open={tagsDialogOpen}
        onClose={handleCloseTagsDialog}
        onCloseConfirmation={handleCloseConfirmation}
        confirm={handleConfirm}
        showConfirmation={showConfirmation}
        withConfirmation={true}
        onSubmit={handleSubmitTags}
        selectedRows={[{ id: workflowId, tags: [] }]}
        getEntityId={(row) => row.id}
        TagPickerComponent={WorkflowTagPickerAdapter}
        loading={updateTagsLoading}
        entityName="Workflow"
        localStorageKey="workflow-recent-tags"
        commonTags={["compliance", "engineering", "linear"]}
      />
    </>
  );
};
